import React, { ChangeEvent } from 'react';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import { ActionLink, Box, Icon, Typography } from 'app/shared/components/atoms';
import { ActionButton, Textfield } from 'app/shared/components/molecules';
import { PromoCodeStatus } from 'app/fan/utils/checkout';
import { ReactComponent as Tag } from 'icons/tag.svg';

interface PromoCodeInputProps {
  isExpressCheckout: boolean;
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  promoCodeStatus: PromoCodeStatus;
  resetValidated: () => void;
  onClickApplyPromoCode: () => void;
  onPromoCodeRemove?: () => void;
  canRedeemAndIsRedeemingVisaFreeTicket: boolean;
  promoCodeErrorMessage: string | null;
  wasValidatePromoCodeCalled: boolean;
}

export const PromoCodeInput: React.FC<
  React.PropsWithChildren<PromoCodeInputProps>
> = ({
  isExpressCheckout,
  inputValue,
  setInputValue,
  promoCodeStatus,
  resetValidated,
  onClickApplyPromoCode,
  onPromoCodeRemove,
  canRedeemAndIsRedeemingVisaFreeTicket,
  promoCodeErrorMessage,
  wasValidatePromoCodeCalled,
}) => {
  const { colors } = useCurrentTheme();
  const { formatMessage } = useIntl();

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const newInputValue = event.target.value.trim();
    if (inputValue !== newInputValue) {
      setInputValue(newInputValue);
      resetValidated();
    }
  }

  function renderRemovePromoCodeActionLink() {
    if (!onPromoCodeRemove) {
      return null;
    }

    return (
      <ActionLink
        onClick={onPromoCodeRemove}
        textDecoration="underline"
        fontSize="fontSize50"
      >
        {formatMessage({ id: 'checkout.payment.promoCode.remove' })}
      </ActionLink>
    );
  }

  const determineHelperText = () => {
    if (canRedeemAndIsRedeemingVisaFreeTicket) {
      if (isExpressCheckout) {
        return (
          <Typography color={colors.messaging.success.dark}>
            {formatMessage({ id: 'event.expressCheckout.promoCode.visaPromo' })}
          </Typography>
        );
      }

      return (
        <Box data-qaid="promocode-visa-info">
          <Typography fontSize="fontSize50">
            {formatMessage({ id: 'checkout.payment.promoCode.visaInfo1' })}
          </Typography>
          <Typography fontSize="fontSize50">
            {formatMessage({ id: 'checkout.payment.promoCode.visaInfo2' })}
          </Typography>
        </Box>
      );
    }

    if (promoCodeStatus === 'ERROR' && wasValidatePromoCodeCalled) {
      return (
        <Box>
          {promoCodeErrorMessage}&nbsp;
          {renderRemovePromoCodeActionLink()}
        </Box>
      );
    }

    if (promoCodeStatus === 'SUCCESS') {
      return (
        <Box gap={1}>
          <Icon
            icon={Tag}
            size="sm"
            strokeColor={colors.messaging.success.dark}
            fillColor="none"
          />
          <Box as="span">
            {formatMessage(
              { id: 'checkout.payment.promoCode.success' },
              { code: inputValue.toUpperCase() }
            )}
            {renderRemovePromoCodeActionLink()}
          </Box>
        </Box>
      );
    }
    if (
      !canRedeemAndIsRedeemingVisaFreeTicket &&
      (promoCodeStatus === 'NONE' || promoCodeStatus === 'LOADING')
    ) {
      return formatMessage({
        id: isExpressCheckout
          ? 'event.expressCheckout.promoCode.disclaimer'
          : 'checkout.payment.promoCode.disclaimer',
      });
    }

    return;
  };

  return (
    <>
      <Box
        flexDirection="row"
        alignItems="flex-start"
        flexWrap="wrap"
        columnGap={2}
        rowGap={0}
      >
        <Box flex={1} minWidth="145px">
          <Textfield
            data-qaid="promo-code-input"
            label={formatMessage({ id: 'checkout.payment.promoCode.enter' })}
            value={inputValue}
            onChange={handleInputChange}
            displayInputValueResetButton={true}
            onResetButtonClick={onPromoCodeRemove}
            isDisabled={promoCodeStatus === 'SUCCESS'}
            hasError={promoCodeStatus === 'ERROR' && wasValidatePromoCodeCalled}
            isSuccessful={promoCodeStatus === 'SUCCESS'}
            helperText={determineHelperText()}
            textTransform="uppercase"
            fontSize="fontSize75"
          />
        </Box>
        {promoCodeStatus !== 'SUCCESS' && promoCodeStatus !== 'ERROR' && (
          <ActionButton
            mt={5}
            onClick={onClickApplyPromoCode}
            isLoading={promoCodeStatus === 'LOADING'}
            data-qaid="promo-code-apply-button"
          >
            {formatMessage({ id: 'checkout.payment.promoCode.apply' })}
          </ActionButton>
        )}
      </Box>
    </>
  );
};
